/**
 * Subscription Confirmation Page Queries
 * 
 * React Query hooks for the subscription confirmation page using centralized query keys.
 * Returns discriminated union types for simplified type handling.
 * 
 * @module subscription/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { useClubId } from '~/services/appRuntime';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { userPassService } from '../api/userPassService';

/**
 * Hook to fetch user pass by ID
 * 
 * Used in the subscription confirmation page to fetch purchased pass details.
 * 
 * **Extended Return Pattern**: Returns all React Query properties plus convenience fields.
 * 
 * @param userId - User ID (for proper scoping)
 * @param userPassId - User pass ID
 * @returns React Query result with ApiResult<GetUserPassByIdPayload>, plus:
 * - `userPass`: Convenience field for unwrapped pass data (null if not loaded or error)
 * - All standard useQuery properties (`data`, `isLoading`, `error`, `isFetching`, `refetch`, etc.)
 * 
 * @example
 * ```typescript
 * const { data, userPass, isLoading } = useUserPassQuery(123, 456);
 * 
 * // Access via convenience field
 * if (userPass) {
 *   console.log(userPass.pass.name);
 * }
 * 
 * // Or access via data (full ApiResult)
 * if (data?.success) {
 *   console.log(data.payload.userPass.pass.name);
 * }
 * ```
 */
export function useUserPassQuery(
  userId: number,
  userPassId: number,
  enabled: boolean = true
) {
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;

  const isEnabled = !!clubIdNumber && !!userId && userId > 0 && !!userPassId && userPassId > 0 && enabled;

  const query = useQuery({
    queryKey: clubIdNumber
      ? queryKeys.passes.userDetail(clubIdNumber, userId, userPassId)
      : queryKeys.disabled.passes(),
    queryFn: () => userPassService.getUserPassById(userPassId),
    enabled: isEnabled,
    ...cachePresets.stable,
    retry: 1,
  });

  return {
    ...query,
    userPass: query.data?.success ? query.data.payload.userPass : null,
  };
}

// The membership receipt reads the member's own card off `useActiveSubscription`'s
// `holdings` (subscription unification D12.4); the per-row endpoint it used to
// fetch here was never mounted.
